/**
 * PHASE 8 — tap-to-identify.
 *
 * Converts a tapped screen pixel into the nearest catalog object, mirroring
 * the GPU pipeline exactly: pixel -> device ray -> world(ENU) direction -->
 * horizontal alt/az -> of-date equatorial -> J2000 (inverse precession) -->
 * nearest star/deep-sky search.
 *
 * Pure and unit-testable; the DOM wiring lives in main.ts.
 *
 * The pick pipeline mirrors the GPU one exactly:
 *   pixel -> device ray -> world(ENU) via transpose(camera rotation) ->
 *   EQD via transpose(ENU-frame rotation, built from lat+LST) ->
 *   J2000 via inverse precession -> nearest catalog object.
 */

import type { ObserverTime } from "../astro/types";
import { ofDateToJ2000Rotation } from "../astro/engine";
import { deepSkyObjects, starCatalog, type StarRecord } from "../catalog/catalog";
import type { Quat } from "../orientation/quaternion";
import type { BodyRenderState } from "../render/dynamicBodies";
import { rotationFromQuat } from "../render/mat4";
import { buildEqdToEnuMatrix, buildPrecessionMatrix, transformDir, transpose33 } from "../render/skyFrame";

export interface PickOptions {
  /** Maximum angular separation from the tap ray that still counts (degrees). */
  toleranceDeg?: number;
  /** Brightest magnitude searched among stars. */
  starLimitMagnitude?: number;
  /** True to include deep-sky objects in the search. */
  includeDeepSky?: boolean;
}

export interface PickResult {
  type: "star" | "deep-sky" | "body";
  id: string;
  name: string;
  magnitude: number;
  /** Angular separation between the tapped direction and the object (degrees). */
  distanceDeg: number;
  /** Catalog-specific extra details for the info card. */
  details?: {
    constellation?: string;
    designation?: string;
    sizeArcmin?: number;
    description?: string;
  };
}

/**
 * Unit direction in the device frame pointing along the tap ray.
 * Pixel (0,0) is the top-left; +x right, +y down, +z out of the screen.
 *
 * The camera/lens looks along the device -z axis (projection.ts, phase 5):
 * the visible sky is the device -z half, exactly what the renderer draws.
 * The ray is therefore built with z = -1 so a tap on a drawn star un-projects
 * to the same world direction as the star that was rasterized there.
 *
 * The ray maps to world space with the same rotations as the renderer:
 * ndcX = 2*x/w - 1, ndcY = 1 - 2*y/h; then y_view = ndcY * tan(fovY/2),
 * x_view = ndcX * tan(fovY/2) * aspect; (x_view, y_view, -1) is the
 * direction in the device frame (x right, y up, z through the back).
 */
export function deviceRayFromPixel(
  pixelX: number,
  pixelY: number,
  widthPx: number,
  heightPx: number,
  fovYDeg: number,
  aspect: number,
): [number, number, number] {
  const ndcX = (2 * pixelX) / widthPx - 1;
  const ndcY = 1 - (2 * pixelY) / heightPx;
  const tanHalf = Math.tan(((fovYDeg / 2) * Math.PI) / 180);
  const x = ndcX * tanHalf * aspect;
  const y = ndcY * tanHalf;
  const len = Math.hypot(x, y, 1);
  return [x / len, y / len, -1 / len];
}

/**
 * Rotate a device-frame direction into the world(ENU) frame using the
 * camera quaternion (world -> device), i.e. the inverse of the renderer's
 * rotation: v_world = R^T * v_device.
 */
export function worldDirectionFromDeviceRay(ray: readonly [number, number, number], qCamera: Quat): [number, number, number] {
  const R = transpose33(rotationFromQuat(qCamera));
  return transformDir(R, ray as [number, number, number]);
}

/** Great-circle angular separation between two J2000 sky positions (degrees). */
export function angularDistanceDeg(raHoursA: number, decDegA: number, raHoursB: number, decDegB: number): number {
  const rad = Math.PI / 180;
  const decA = decDegA * rad;
  const decB = decDegB * rad;
  const raDiff = Math.abs(raHoursA - raHoursB) * 15 * rad;
  const cosSep =
    Math.sin(decA) * Math.sin(decB) + Math.cos(decA) * Math.cos(decB) * Math.cos(raDiff);
  const sep = Math.acos(Math.min(1, Math.max(-1, cosSep)));
  return sep / rad;
}

function starDetails(star: StarRecord): PickResult["details"] {
  return {
    constellation: star.constellation || undefined,
    designation: star.designation || undefined,
  };
}

/**
 * Find the nearest catalog object (star, deep-sky or dynamic body) to a sky
 * position in J2000 coordinates. Returns null when nothing lies within
 * toleranceDeg. The Sun/Moon/planets are searched with the same tolerance,
 * so tapping the drawn Moon sprite identifies the Moon even among stars.
 */
export function findNearestObject(
  raHoursJ2000: number,
  decDegJ2000: number,
  options: PickOptions = {},
  bodies?: readonly BodyRenderState[],
): PickResult | null {
  const tolerance = options.toleranceDeg ?? 2.5;
  const starLimit = options.starLimitMagnitude ?? 6.5;
  const includeDso = options.includeDeepSky ?? true;

  let best: PickResult | null = null;
  if (bodies) {
    for (const body of bodies) {
      const d = angularDistanceDeg(raHoursJ2000, decDegJ2000, body.raHoursJ2000, body.decDegJ2000);
      if (d <= tolerance && (!best || d < best.distanceDeg)) {
        best = {
          type: "body",
          id: body.id,
          name: body.name,
          magnitude: body.magnitude,
          distanceDeg: d,
          details: { description: undefined },
        };
      }
    }
  }
  for (const star of starCatalog.brightStars(starLimit)) {
    const d = angularDistanceDeg(raHoursJ2000, decDegJ2000, star.raHours, star.decDegrees);
    if (d <= tolerance && (!best || d < best.distanceDeg)) {
      best = {
        type: "star",
        id: star.hip ? `STAR:${star.hip}` : `STAR:${star.index}`,
        name: star.name || star.designation || `HIP ${star.hip ?? star.index}`,
        magnitude: star.magnitude,
        distanceDeg: d,
        details: starDetails(star),
      };
    }
  }
  if (includeDso) {
    for (const dso of deepSkyObjects) {
      const d = angularDistanceDeg(raHoursJ2000, decDegJ2000, dso.ra / 15, dso.dec);
      if (d <= tolerance && (!best || d < best.distanceDeg)) {
        best = {
          type: "deep-sky",
          id: `DSO:${dso.id}`,
          name: dso.name,
          magnitude: dso.mag,
          distanceDeg: d,
          details: {
            constellation: dso.con,
            sizeArcmin: dso.sizeArcmin,
          },
        };
      }
    }
  }
  return best;
}

export interface PickAtPixelArgs {
  pixelX: number;
  pixelY: number;
  widthPx: number;
  heightPx: number;
  fovYDeg: number;
  aspect: number;
  qCamera: Quat | null;
  latitudeDeg: number;
  localSiderealHours: number;
  time: ObserverTime;
  options?: PickOptions;
  /** Dynamic-body render states to include in the search (optional). */
  bodies?: readonly BodyRenderState[];
}

/**
 * Full tap pipeline: pixel -> device ray -> world(ENU) -> of-date equatorial
 * -> J2000 -> nearest object. Uses the same matrices as the GPU, so a tap on
 * a drawn star always identifies that star. Returns null when the tap hits
 * nothing (or the camera is unavailable).
 */
export function pickAtPixel(args: PickAtPixelArgs): PickResult | null {
  if (!args.qCamera) return null;
  const ray = deviceRayFromPixel(
    args.pixelX,
    args.pixelY,
    args.widthPx,
    args.heightPx,
    args.fovYDeg,
    args.aspect,
  );
  const world = worldDirectionFromDeviceRay(ray, args.qCamera);
  const enuToEqd = transpose33(buildEqdToEnuMatrix(args.latitudeDeg, args.localSiderealHours));
  const eqd = transformDir(enuToEqd, world);
  const R = ofDateToJ2000Rotation(args.time);
  const j2000 = transformDir(buildPrecessionMatrix(R), eqd);
  const raJ2000 = (Math.atan2(j2000[1], j2000[0]) * (12 / Math.PI) + 24) % 24;
  const decJ2000 = Math.asin(Math.min(1, Math.max(-1, j2000[2]))) * (180 / Math.PI);
  return findNearestObject(raJ2000, decJ2000, args.options, args.bodies);
}